import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { createQuery } from "@tanstack/solid-query";
import { firebase } from "@core/firebase/app";
import { queryKeys } from "@core/query";
import type { LocationAddress, LocationDetail, LocationFloor, LocationFloorApartment, LocationSummary } from "./types";

const LOCATIONS_COLLECTION = "locations";

const readString = (value: unknown, fallback: string | null = null): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return fallback;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const mapAddress = (raw: unknown): LocationAddress => {
  if (typeof raw !== "object" || raw === null) {
    return { street: null, city: null, state: null, postalCode: null };
  }
  const data = raw as Record<string, unknown>;
  return {
    street: readString(data["street"]),
    city: readString(data["city"]),
    state: readString(data["state"]),
    postalCode: readString(data["postalCode"]),
  };
};

const mapApartments = (raw: unknown): LocationFloorApartment[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => {
      if (typeof item !== "object" || item === null) {
        return null;
      }
      const data = item as Record<string, unknown>;
      return {
        unit: readString(data["unit"]),
        squareFeet: readNumber(data["squareFeet"]),
      } satisfies LocationFloorApartment;
    })
    .filter(Boolean) as LocationFloorApartment[];
};

const mapFloors = (raw: unknown): LocationFloor[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => {
      if (typeof item !== "object" || item === null) {
        return null;
      }
      const data = item as Record<string, unknown>;
      return {
        number: readNumber(data["number"]),
        name: readString(data["name"]),
        apartments: mapApartments(data["apartments"]),
      } satisfies LocationFloor;
    })
    .filter(Boolean) as LocationFloor[];
};

const countApartments = (floors: LocationFloor[]): number =>
  floors.reduce((total, floor) => total + floor.apartments.length, 0);

const mapLocationSummary = (id: string, raw: Record<string, unknown>): LocationSummary => {
  const address = mapAddress(raw["address"]);
  const floors = mapFloors(raw["floors"]);
  return {
    id,
    name: readString(raw["name"], id) ?? id,
    serviceStatus: readString(raw["serviceStatus"]),
    city: address.city,
    state: address.state,
    totalFloors: floors.length,
    totalApartments: countApartments(floors),
  };
};

export const fetchLocations = async (): Promise<LocationSummary[]> => {
  const locationsRef = collection(firebase.firestore, LOCATIONS_COLLECTION);
  const locationsQuery = query(locationsRef, orderBy("name"));
  const snapshot = await getDocs(locationsQuery);
  return snapshot.docs.map((docSnap) => mapLocationSummary(docSnap.id, docSnap.data() as Record<string, unknown>));
};

export const fetchLocationDetail = async (locationId: string): Promise<LocationDetail | null> => {
  const locationRef = doc(firebase.firestore, LOCATIONS_COLLECTION, locationId);
  const snapshot = await getDoc(locationRef);
  if (!snapshot.exists()) {
    return null;
  }
  const data = snapshot.data() as Record<string, unknown>;
  const summary = mapLocationSummary(snapshot.id, data);
  const address = mapAddress(data["address"]);
  const floors = mapFloors(data["floors"]);
  return {
    summary,
    address,
    notes: readString(data["notes"]),
    floors,
  };
};

export const useLocationListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.locations.root(),
    queryFn: () => fetchLocations(),
  }));

export const useLocationDetailQuery = (locationId: () => string | null) =>
  createQuery(() => {
    const id = locationId();
    return {
      queryKey: queryKeys.locations.detail(id ?? "detail"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchLocationDetail(id) : Promise.resolve(null)),
    };
  });
